<template>
  <div class="p-3">
    <!-- Formulário de Pagamento -->
    <div class="premium-card shadow-sm mb-4">
      <div class="card-header-premium">
        <i class="fa fa-credit-card mr-2"></i> CONFIGURAÇÕES DE PAGAMENTO
      </div>
      <div class="p-3">
        <b-row class="align-items-end">
          <b-col md="4">
            <b-form-group label="Forma de Pagamento" class="premium-form-group mb-0">
              <b-form-select v-model="nfe.OP_FORMA_PAGAMENTO" :options="formaPagamentoOptions" class="rounded-pill" :disabled="!podeEditar">
                <template #first>
                  <b-form-select-option :value="null">Selecione</b-form-select-option>
                </template>
              </b-form-select>
            </b-form-group>
          </b-col>

          <b-col md="2">
            <b-form-group label="Quantas Parcelas" class="premium-form-group mb-0">
              <b-form-input 
                v-model.number="nfe.NR_PARCELAS" 
                type="number" 
                min="1" 
                class="rounded-pill"
                :disabled="!podeEditar || nfe.OP_FORMA_PAGAMENTO !== '1'"
              />
            </b-form-group>
          </b-col>

          <b-col md="auto">
            <b-button 
              variant="primary" 
              class="rounded-pill px-4 shadow-sm"
              @click="gerarParcelamento"
              :disabled="!podeEditar || !podeGerarParcelamento || loading"
            >
              <i class="fa fa-magic mr-1"></i> Gerar Parcelamento
            </b-button>
          </b-col>

          <b-col md="auto">
            <b-button 
              variant="outline-danger" 
              class="rounded-pill px-4 shadow-sm"
              @click="excluirParcelamento"
              :disabled="!podeEditar || parcelas.length === 0 || loading"
            >
              <i class="fa fa-trash mr-1"></i> Excluir Parcelamento
            </b-button>
          </b-col>
        </b-row>
      </div>
    </div>

    <!-- Tabela de Parcelas -->
    <div class="premium-card shadow-sm">
      <div class="card-header-premium d-flex justify-content-between align-items-center">
        <span><i class="fa fa-list-ol mr-2"></i> FATURA / DUPLICATAS</span>
        <b-badge variant="light" pill class="px-3 py-2 text-primary shadow-sm">
          {{ parcelas.length }} Parcela(s)
        </b-badge>
      </div>
      <div class="p-0">
        <b-overlay :show="loading" opacity="0.6" rounded="sm">
          <b-table 
            class="premium-table mb-0"
            :items="parcelas" 
            :fields="fields" 
            responsive
            hover
            show-empty
            empty-text="Nenhuma parcela gerada."
          >
            <template #cell(parcela)="data">
              <span class="font-weight-bold text-primary">{{ data.item.NR_PARCELA || data.index + 1 }}</span>
            </template>
            <template #cell(numero)="data">{{ data.item.TX_NUMERO || '-' }}</template>
            <template #cell(vencimento)="data">
              <span class="text-info"><i class="fa fa-calendar-o mr-1"></i> {{ formatDate(data.item.DT_VENCIMENTO) }}</span>
            </template>
            <template #cell(valorTotal)="data">
              <span class="font-weight-bold">{{ formatCurrency(data.item.VL_TOTAL) }}</span>
            </template>
            <template #cell(actions)="data">
              <b-button 
                size="sm" 
                variant="outline-primary" 
                class="rounded-pill px-3"
                @click="alterarDataVencimento(data.item)"
                :disabled="!podeEditar || loading"
              >
                <i class="fa fa-edit mr-1"></i> Alterar Vencimento
              </b-button>
            </template>
          </b-table>
        </b-overlay>
      </div>
    </div>

    <!-- Modal de Edição de Parcela -->
    <parcela-modal
      ref="parcelaModal"
      :parcela="parcelaEditando"
      @salvar="onSalvarParcela"
    />
  </div>
</template>

<script>
import ParcelaModal from './ParcelaModal.vue'
import Tools from '@/tools/tools.js'

export default {
  name: 'NfeAbaCobranca',
  components: { ParcelaModal },
  props: { 
    nfe: { type: Object, required: true },
    podeEditar: { type: Boolean, default: false }
  },
  data() {
    return {
      parcelas: [],
      loading: false,
      parcelaEditando: null,
      formaPagamentoOptions: [
        { value: '0', text: '0 - Pagamento À Vista' },
        { value: '1', text: '1 - Pagamento A Prazo' },
        { value: '2', text: '2 - Nenhum' },
        { value: '3', text: '3 - Outros' }
      ],
      fields: [
        { key: 'parcela', label: 'Parcela', thStyle: { width: '8%' } },
        { key: 'numero', label: 'Número', thStyle: { width: '30%' } },
        { key: 'vencimento', label: 'Vencimento', thStyle: { width: '22%' } },
        { key: 'valorTotal', label: 'Valor Total', class: 'text-right', thStyle: { width: '15%' } },
        { key: 'actions', label: '', class: 'text-right', thStyle: { width: '25%' } }
      ]
    }
  },
  computed: {
    podeGerarParcelamento() {
      return this.nfe.OP_FORMA_PAGAMENTO === '1' && 
             this.nfe.NR_PARCELAS > 0 && 
             this.nfe.ID
    }
  },
  async mounted() {
    await this.carregarParcelas()
  },
  watch: {
    'nfe.ID': {
      handler(newVal) {
        if (newVal) {
          this.carregarParcelas()
        }
      }
    }
  },
  methods: {
    async carregarParcelas() {
      if (!this.nfe.ID) return
      
      try {
        this.loading = true
        const resp = await this.$http.get(`/api/protected/notaFiscal/duplicata/getByNfeId/${this.nfe.ID}`)
        this.parcelas = resp.data || []
      } catch (err) {
        Tools.exibirMensagem(this.$store, 'Aviso', 'danger', err)
      } finally {
        this.loading = false
      }
    },
    
    async gerarParcelamento() {
      if (!this.podeGerarParcelamento) {
        this.toast('Informe a forma de pagamento e número de parcelas', 'warning')
        return
      }

      const confirmado = await this.$bvModal.msgBoxConfirm(
        `Gerar parcelamento em ${this.nfe.NR_PARCELAS} parcela(s)?`,
        {
          title: 'Confirmar Geração',
          okVariant: 'primary',
          okTitle: 'Sim',
          cancelTitle: 'Não'
        }
      )
      
      if (!confirmado) return

      try {
        this.loading = true
        
        await this.$http.post(`/api/protected/notaFiscal/duplicata/gerar/`, {
          nfeId: this.nfe.ID,
          parcelas: this.nfe.NR_PARCELAS
        })

        this.toast('Parcelamento gerado com sucesso', 'success')
        
        // Recarregar NFe e parcelas
        await this.recarregarNfe()
        await this.carregarParcelas()
        
      } catch (err) {
        Tools.exibirMensagem(this.$store, 'Aviso', 'danger', err)
      } finally {
        this.loading = false
      }
    },

    async excluirParcelamento() {
      if (this.parcelas.length === 0) return

      const confirmado = await this.$bvModal.msgBoxConfirm(
        'Deseja realmente excluir todo o parcelamento?',
        {
          title: 'Confirmar Exclusão',
          okVariant: 'danger',
          okTitle: 'Sim',
          cancelTitle: 'Não'
        }
      )
      
      if (!confirmado) return

      try {
        this.loading = true
        
        await this.$http.post('/api/protected/notaFiscal/duplicata/deleteAll', {
          nfeId: this.nfe.ID
        })

        this.toast('Parcelamento excluído com sucesso', 'success')
        
        // Recarregar apenas as parcelas
        await this.carregarParcelas()
        
      } catch (err) {
        Tools.exibirMensagem(this.$store, 'Aviso', 'danger', err)
      } finally {
        this.loading = false
      }
    },

    async alterarDataVencimento(parcela) {
      try {
        this.loading = true
        
        // Buscar dados completos da parcela
        const resp = await this.$http.get(`/api/protected/notaFiscal/duplicata/getById/${parcela.ID}`)
        const data = resp.data
        
        // Backend retorna array, pegar primeiro elemento
        this.parcelaEditando = Array.isArray(data) ? data[0] : data
        
        // Abrir modal
        this.$nextTick(() => {
          this.$refs.parcelaModal.mostrar()
        })
        
      } catch (err) {
        Tools.exibirMensagem(this.$store, 'Aviso', 'danger', err)
      } finally {
        this.loading = false
      }
    },

    async onSalvarParcela(parcelaAtualizada) {
      try {
        this.loading = true
        
        await this.$http.post('/api/protected/notaFiscal/duplicata/edit', parcelaAtualizada)
        
        this.toast('Parcela atualizada com sucesso', 'success')
        
        // Recarregar parcelas
        await this.carregarParcelas()
        
      } catch (err) {
        Tools.exibirMensagem(this.$store, 'Aviso', 'danger', err)
      } finally {
        this.loading = false
      }
    },

    async recarregarNfe() {
      try {
        const nfeAtualizada = await this.$store.dispatch('nfe/GET_BY_ID', this.nfe.ID)
        this.$emit('nfe-atualizada', nfeAtualizada)
      } catch (err) {
        console.error('Erro ao recarregar NF-e:', err)
      }
    },

    formatCurrency(v) { 
      return Tools.formatCurrency(v)
    },
    
    formatDate(d) { 
      return Tools.formatarDataBR(d)
    },

    toast(msg, variant = 'info') {
      this.$bvToast && this.$bvToast.toast(msg, {
        title: 'Cobrança',
        variant,
        autoHideDelay: 3500,
        solid: true
      })
    }
  }
}
</script>

<style scoped>
</style>



// WEBPACK FOOTER //
// src/views/faturamento/nfe/partials/NfeAbaCobranca.vue